import { Grid, Link } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import ErrorIcon from '@mui/icons-material/Error';
import React from 'react';
import parse from 'html-react-parser';
import HlxButton from '../../commonComponents/button/button';
import Acknowledgement, { NoScanReason } from './acknowledgement';
import AssignmentError from './assignmentError';
import LostStolenDetail from './lostStolenDetail';
import NewDevice from './newDevice';
import PcBreakFix from './pcBreakFix';
import PCRefreshDetail from './pcRefresh';
import PCReturn from './pcReturn';
import RegisterDevice from './registerDevice';
import { StyledTicketDetail } from './ticketDetail.style';
import HyperLink from "../../../component/commonComponents/hyperLink/hyperLink";
import { NOT_APPLICABLE } from '../../../constants/originalState';
import NewVirtualDevice from './newVirtualDevice';
import { StyledOrderNumber } from 'component/MarketPlace/OrderItemCard/index.style';
import { useTheme } from 'styled-components';
import DefaultUptimeTheme from 'component/theme/defaultTheme';
import { StyledTooltip } from 'component/cardComponent/pcCard/pcCard.style';
import AppRequest from './appRequest';
import Typography from 'component/MarketPlace/Typography';
import { capitalizeFirstLetter } from 'utils/functions';

export const TICKET_REQUEST_TYPE = {
  PC_LOST_STOLEN: 'pc_lost_stolen',
  PC_ACKNOWLEDGE_DEVICE: 'pc_acknowledge_device',
  PC_REFRESH: 'pc_refresh',
  PC_RETURN: 'pc_return',
  PC_ASSIGNMENT_ERROR: 'pc_assignment_error',
  PC_BREAKFIX: 'pc_breakfix',
  NEW_DEVICE: 'new_device',
  REGISTER_DEVICE: 'register_device',
  NEW_VDA: 'vda_request',
  VA_REQUEST: 'va_request',
  APP_REQUEST: 'app_request'
};

export interface ITicketDetailProps {
  infoTicketDetail: any,
  createDate: any,
  onResolvedClick?: () => void,
  onReopenClick?: () => void,
  onCancelClick?: () => void,
  isLoadingTicketDetail?: boolean,
  isTicket?: string,
  KBPcCost: any,
  ticketCatalog: any,
  vdaticketCatalog: string,
  locale: any,
  isUnableCancel?: boolean,
  canCancelVisible?: boolean,
  canCancelConfig?: boolean,
  isCheckBtnResolve?:boolean,
  isCheckBtnReopen?: boolean,
  snowLinkTicket?: any,
  noScanReasons?: NoScanReason[];
};

const TicketDetail = (props: ITicketDetailProps) => {

  const {
    infoTicketDetail,
    createDate,
    onResolvedClick,
    onReopenClick,
    onCancelClick,
    isLoadingTicketDetail,
    isTicket = 'request',
    KBPcCost = '',
    ticketCatalog,
    vdaticketCatalog,
    locale = {},
    isUnableCancel = true,
    canCancelVisible,
    canCancelConfig,
    isCheckBtnResolve,
    isCheckBtnReopen,
    snowLinkTicket,
    noScanReasons,
  } = props;
  const {canResolve, canReopen , canCancel} = infoTicketDetail?.actions;
  const variableInfo = infoTicketDetail?.variables;
  const isPCLifecycle = ticketCatalog === infoTicketDetail?.catalog?.id;
  const isVDASoftware = vdaticketCatalog === infoTicketDetail?.catalog?.id;
  let subTitleText : any = null;
  let buttonNameText : any = null;
  let commonStatusText : any = null;

  const isRequestType = (key: keyof typeof TICKET_REQUEST_TYPE) => {
    const requestType = variableInfo?.json_storage?.request_type;
    return TICKET_REQUEST_TYPE[key] === requestType;
  };

  const getTicketValue = (key: string, format?: "uppercase" | "lowercase") => {
    const value = !!infoTicketDetail[key]
      ? infoTicketDetail[key]
      : NOT_APPLICABLE;

    if (format === "uppercase") {
      return value.toUpperCase();
    }

    if (format === "lowercase") {
      return value.toLowerCase();
    }

    return value;
  };

  const getStatusText = (status: string) => commonStatusText?.[status?.toUpperCase() || ''] || status?.toUpperCase();

  if(locale){
    const { subTitle, buttonName, commonStatus } = locale;
    subTitleText = subTitle;
    buttonNameText = buttonName;
    commonStatusText = commonStatus;
  };
  const cancelReason = infoTicketDetail?.variables?.cancel_reason;
  const isNewDevice = isRequestType("NEW_DEVICE");
  const isNewVda = isRequestType("NEW_VDA");
  const isAppRequest = isRequestType("APP_REQUEST");
  const isVA = isRequestType("VA_REQUEST");
  const isRegisterDevice = isRequestType("REGISTER_DEVICE");
  const theme = useTheme() as typeof DefaultUptimeTheme;

  const configTracking = (string: string) => {
    if (!!string) {
      if (string.indexOf('http') > -1) {
        return <HyperLink
          label={isPCLifecycle ? locale?.clickToTrack : locale?.link}
          onClick={() => window.open(string, '_blank')}
        />
      }
      return `${string}`;
    }
    return NOT_APPLICABLE;
  };

  const loadRequestTicket = () => {
    switch (variableInfo?.json_storage?.request_type) {
      case TICKET_REQUEST_TYPE.PC_REFRESH:
        return <PCRefreshDetail infoTicket = {infoTicketDetail} configTracking = {configTracking} locale = {locale} />;
      case TICKET_REQUEST_TYPE.PC_LOST_STOLEN:
        return <LostStolenDetail infoTicket = {infoTicketDetail} configTracking = {configTracking} locale = {locale} />;
      case TICKET_REQUEST_TYPE.PC_ACKNOWLEDGE_DEVICE:
        return <Acknowledgement infoTicket={infoTicketDetail} noScanReasons={noScanReasons} locale={locale} />;
      case TICKET_REQUEST_TYPE.PC_RETURN:
        return <PCReturn infoTicket={infoTicketDetail} configTracking={configTracking} locale = {locale} />;
      case TICKET_REQUEST_TYPE.PC_ASSIGNMENT_ERROR:
        return <AssignmentError infoTicket = {infoTicketDetail} locale = {locale} />;
      case TICKET_REQUEST_TYPE.PC_BREAKFIX:
        return (
          <PcBreakFix
            infoTicket={infoTicketDetail}
            configTracking={configTracking}
            locale = {locale}
          />
        );
      case TICKET_REQUEST_TYPE.NEW_DEVICE:
        return <NewDevice infoTicket = {infoTicketDetail} KBPcCost={KBPcCost} locale = {locale} configTracking={configTracking} />
      case TICKET_REQUEST_TYPE.REGISTER_DEVICE:
        return <RegisterDevice infoTicket = {infoTicketDetail} locale = {locale}/>
      case TICKET_REQUEST_TYPE.NEW_VDA:
        return <NewVirtualDevice infoTicket={infoTicketDetail} locale={locale}/>
      case TICKET_REQUEST_TYPE.VA_REQUEST:
        return <NewVirtualDevice infoTicket={infoTicketDetail} locale={locale}/>
      case TICKET_REQUEST_TYPE.APP_REQUEST:
        return <AppRequest infoTicket={infoTicketDetail} locale={locale} />
      default:
        return null;
    }
  };

  const stateValue = getStatusText(infoTicketDetail?.state) || NOT_APPLICABLE;
  const stageINCValue = stateValue !== NOT_APPLICABLE ? commonStatusText?.[stateValue === "OPEN" ? "SUBMITTED" : "RESOLVED"] : NOT_APPLICABLE;
  const stageValue = getStatusText(variableInfo?.stage?.display_value) || getStatusText(infoTicketDetail?.originalState) || NOT_APPLICABLE;
  const stateSubtitle = isNewDevice ? subTitleText?.status : subTitleText?.state;
  const createdDateSubtitle = isNewDevice || isNewVda || isAppRequest || isVA ? subTitleText?.requested : subTitleText?.created;

  const renderInfoTicket = (visible_variables: any, variables: any) => {
    return visible_variables?.map((field: any) => (
      <Grid item xs={6} className="pr-20">
        <p className="ttl-col-desc">{capitalizeFirstLetter(field?.label)}</p>
        <p className="value-info" id={`lbl-ticketdetail-${field?.id}`}>{parse(variables?.[field?.name]?.toString() || 'N/A')}</p>
      </Grid>
    ));
  };

  return (
    <StyledTicketDetail>
      {isLoadingTicketDetail ?
        <CircularProgress className="isLoadingTicketDetail" />
        :
        <>
          <StyledTooltip title={infoTicketDetail?.shortDescription} placement="bottom-start">
            <StyledOrderNumber
              variant="h4"
              textAlign="left"
              maxlines={1}
              fontSize={`${theme.uptimeFontSizes.desktop.fz9}px`}
              fontWeight={700}
              color={theme.uptimeColors.grayScales.black}
              margin={`${theme.uptimeSpace[0]}`}
              paddingRight={`${theme.uptimeSpace[20]}px`}
              id="lbl-ticketdetail-shortdescription"
            >
              {infoTicketDetail?.shortDescription}
            </StyledOrderNumber>
          </StyledTooltip>
          <Grid container>
            <Grid item xs={6} className="pr-20">
              <p>
                <span className="ttl-bold">{stateSubtitle}</span>
                <span className={`value state ${infoTicketDetail.state}`} id="lbl-ticketdetail-state">{stateValue}</span>
              </p>
            </Grid>
            <Grid item xs={6}>
              <p>
                <span className="ttl-bold">{subTitleText?.stage}</span>
                <span className={`value state ${variableInfo?.stage?.value}`} id="lbl-ticketdetail-display_value">{isTicket === "INC" ? stageINCValue : stageValue}</span>
              </p>
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={6}>
              <p>
                <span className="ttl-bold">{createdDateSubtitle}</span>
                <span className="value" id="lbl-ticketdetail-create_date">{createDate || NOT_APPLICABLE}</span>
              </p>
            </Grid>

            <Grid item xs={6} className="pr-20">
              <p>
                <span className="ttl-bold">{isTicket === "INC" ? subTitleText?.incident : subTitleText?.request} {subTitleText?.ticketNumber}</span>
                {snowLinkTicket?.enabled ? (
                  <Link
                    onClick={() =>
                      window.open(
                        isTicket === "INC"
                          ? snowLinkTicket?.snowUrlIncident?.replace(
                              "{ticketId}",
                              infoTicketDetail.id
                            )
                          : snowLinkTicket?.snowUrlRequest?.replace(
                              "{ticketId}",
                              infoTicketDetail.id
                            ),
                        "_blank"
                      )
                    }
                    sx={{ cursor: "pointer", textDecoration: "none !important" }}
                  >
                  <Typography 
                    variant="body2"
                    sx={{ 
                      color: theme.uptimeColors.generalColors.button.hyperlink.enabled,
                      "&:hover": {
                        color: theme.uptimeColors.generalColors.button.hyperlink.hover,
                      }
                    }} 
                    fontWeight={600}
                    id="lbl-ticketdetail-number"
                  >
                    {getTicketValue("number")}
                  </Typography>
                </Link>
                ) : (
                  <span className="value" id="lbl-ticketdetail-number">{getTicketValue("number")}</span>
                )}
              </p>
            </Grid>

            {!isPCLifecycle && (
              <Grid item xs={6}>
                <p>
                  <span className="ttl-bold">{subTitleText?.priority}</span>
                  <span className="value" id="lbl-ticketdetail-priority">{getTicketValue("priority")}</span>
                </p>
              </Grid>
            )}
          </Grid>

          {
            canResolve ? (
              isCheckBtnResolve && 
              <Grid container style={{ margin: '15px 10px 0 0' }}>
                <HlxButton
                  label={buttonNameText?.resolve}
                  onClick={onResolvedClick}
                  id="btn-ticketdetail-resolve_reopen"
                />
              </Grid>
            ): canReopen && (
              isCheckBtnReopen &&
              <Grid container style={{ margin: '15px 10px 0 0' }}>
                <HlxButton
                  label={buttonNameText?.reopen}
                  onClick={onReopenClick}
                  id="btn-ticketdetail-resolve_reopen"
                />
              </Grid>
            )
          }

          {(canCancel && canCancelVisible && canCancelConfig) && (
            <Grid container style={{ margin: '15px 10px 0 0' }}>
              <HlxButton
                label={buttonNameText?.cancel}
                variant='contained'
                onClick={onCancelClick}
                id="lbl-ticketdetail-cancel"
              />
            </Grid>
          )}

          {/* unable to cancel message */}
          {isUnableCancel && <Grid container className="unable-cancel">
            <ErrorIcon className="unable-cancel-icon"/>
            <div className="unable-cancel-message" id="lbl-ticketdetail-unable_cancel">{locale?.unableCancelMessage}</div>
          </Grid>}

          <div className="desc-ticket">
          {(!isNewVda && !isVA && !isAppRequest) &&
              <>
                <Grid container>
                  <Grid item xs={6} className="pr-20">
                    <p className="ttl-col-desc">{locale?.requestedFor}</p>
                    <p className="value-info" id="lbl-ticketdetail-requestfor">{getTicketValue("requestFor")}</p>
                  </Grid>
                  <Grid item xs={6}>
                    <p className="ttl-col-desc">{locale?.location}</p>
                    <p className="value-info" id="lbl-ticketdetail-location">{getTicketValue("location")}</p>
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid item xs={6} className="pr-20">
                    <p className="ttl-col-desc">{locale?.email}</p>
                    <p className="value-info" id="lbl-ticketdetail-email">{getTicketValue("email")}</p>
                  </Grid>
                  <Grid item xs={6}>
                    <p className="ttl-col-desc">{locale?.contactNumber}</p>
                    <p className="value-info" id="lbl-ticketdetail-telephone">{getTicketValue("telephone")}</p>
                  </Grid>
                </Grid>
              </>
            }
            {isTicket === "INC" ? (
              <Grid container>
                <Grid item xs={6} className="pr-20">
                  <p className="ttl-col-desc">{locale?.typeOfIssue}</p>
                  <p className="value-info" id="lbl-ticketdetail-category">{getTicketValue("category")}</p>
                </Grid>
                <Grid item xs={6}>
                  <p className="ttl-col-desc">{locale?.moreDetails}</p>
                  <p className="value-info" id="lbl-ticketdetail-subcategory">{getTicketValue("subcategory")}</p>
                </Grid>
                <Grid item xs={12}>
                  <p className="ttl-col-desc">{locale?.description}</p>
                  <p className="value-info" id="lbl-ticketdetail-description">{getTicketValue("description")}</p>
                </Grid>
              </Grid>
            ) : !!isPCLifecycle || !!isVDASoftware ? (
              loadRequestTicket()
            ) : (
              <Grid container>
                <Grid item xs={6} className="pr-20">
                  <p className="ttl-col-desc">{locale?.requestedItem}</p>
                  <p className="value-info" id="lbl-ticketdetail-catalog_name">
                    {!!infoTicketDetail.catalog?.name ? infoTicketDetail.catalog?.name : NOT_APPLICABLE}
                  </p>
                </Grid>
                <Grid item xs={6}>
                  <p className="ttl-col-desc">{locale?.moreDetails}</p>
                  <p className="value-info" id="lbl-ticketdetail-description">{getTicketValue("description")}</p>
                </Grid>
              </Grid>
            )}

            {infoTicketDetail?.visible_variables?.length > 0 && (
              <Grid container>
                {renderInfoTicket(infoTicketDetail?.visible_variables, variableInfo)}
              </Grid>
            )}

            {(isRegisterDevice && variableInfo?.closure_reason) && (
              <div className="closure-reason">
                <p className="ttl-col-desc">{locale?.closureReason}</p>
                <p className="value-info" id="lbl-ticketdetail-closure_reason">{parse(variableInfo.closure_reason.replace(/\n/g, '<br>'))}</p>
              </div>
            )}
            {cancelReason && <div>
                <p className="ttl-col-desc">{locale?.cancelReasonComment}</p>
                <p className='value-info' id="lbl-ticketdetail-cancel_reason">
                  {
                    cancelReason.toLowerCase().startsWith('other') ?
                    parse(
                      cancelReason.replace('<br />','').replace(cancelReason.substring(0, 5), cancelReason.substring(0, 5) + ' - ').replace(/\n/g, '')
                    ) :
                    parse(cancelReason.replace(/\n/g, '<br>'))
                  }
                </p>
              </div>}
          </div>
        </>
      }
    </StyledTicketDetail>
  );
};

export default TicketDetail;